import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import {
  getSelectedCase,
  getSingleParty,
  setToggle,
  toggleState,
} from "../../../states/slices/partyInfo.slice";
import { TableComponent } from "../../../components/table/Table";
import { PARTY_COLUMNS_view } from "../../../components/table/columns";
import Typography from "../../../components/Typography";
import { FLAG, PDF, UPLOAD } from "../../../assets";
import {
  formatCurrency,
  getFormDataHeader,
  getJSONheader,
} from "../../../utils";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "../../../components/button";
import Api from "../../../config";
import { useDropzone } from "react-dropzone";
import Modal from "../../../components/UI/modal";
import { Fade, Zoom } from "react-reveal";
import { FormInput } from "../../../components/form/Input";
import { FaRegTrashAlt } from "react-icons/fa";
import { toast } from "react-hot-toast";
import PartyInformation from "../../../components/modals/Partinformation";

function ProceedPayment() {
  const getCase = useAppSelector(getSelectedCase);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(3);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [file, setFile] = useState<any>([]);
  const [title, setTitle] = useState<any>("");
  const [paymentID, setPaymentID] = useState<any>();
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);
  const [required, setRequired] = useState("");
  const [requiredPhoto, setRequiredPhoto] = useState("");
  const toggleStatus = useAppSelector(toggleState);
  const singlePartyInfo = useAppSelector(getSingleParty);
  const dispatch = useAppDispatch();
  const [loading, setIsLoading] = useState<boolean>(false);
  const [activeCaseInView, setActiveCaseInView] = useState<any>(null);
  const [isLoadingCaseFolder, setIsLoadingCaseFolder] = useState(false);
  const caseId = searchParams.get("id") || getCase?.id;
  const caseData = activeCaseInView || getCase;
  const [docTab, setDocTab] = useState<"ALL" | "CLAIMANT" | "DEFENDANT">("ALL");

  const claimantDocs = (caseData?.case_documents || []).filter((doc: any) => {
    const title = (doc?.party_title || "").toLowerCase();
    return title.includes("claimant") || title.includes("plaintiff");
  });
  const defendantDocs = (caseData?.case_documents || []).filter((doc: any) => {
    const title = (doc?.party_title || "").toLowerCase();
    return title.includes("defendant");
  });
  const displayedDocs =
    docTab === "CLAIMANT"
      ? claimantDocs
      : docTab === "DEFENDANT"
      ? defendantDocs
      : caseData?.case_documents || [];

  const showCaseFolder = async () => {
    if (!caseId) return;
    setIsLoadingCaseFolder(true);
    try {
      const response = await Api.get(
        `/lawyer/case-folders/${caseId}`,
        getJSONheader(),
      );
      setActiveCaseInView(response?.data?.data);
    } catch (error) {
      console.error("Error fetching case folder:", error);
    } finally {
      setIsLoadingCaseFolder(false);
    }
  };

  useEffect(() => {
    showCaseFolder();
  }, [caseId]);

  const fetchPayments = async (caseFolderId: string) => {
    setIsLoadingPayments(true);
    try {
      const response = await Api.get(`/payments`, getJSONheader());
      const payments = Array.isArray(response?.data?.data)
        ? response.data.data
        : [];
      const matchedPayment = payments.find(
        (item: any) => item?.service?.id && item?.service?.id === caseFolderId,
      );
      setPaymentID(matchedPayment);
    } catch (error) {
      console.error("Error fetching payments:", error);
    } finally {
      setIsLoadingPayments(false);
    }
  };

  useEffect(() => {
    if (!caseId) return;
    fetchPayments(caseId);
  }, [caseId]);

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    maxSize: 50000000,
    accept: {
      "image/*": [".png, .jpeg, .jpg, .svg"],
    },
    onDrop: (acceptedFiles) => {
      setFile(
        acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          }),
        ),
      );
      setRequiredPhoto("");
    },
  });

  const productFiles = file.map((item: any, index: number) => (
    <div className="lg:w-[30vw] flex justify-between items-center">
      <div className="flex justify-start items-center ">
        <img src={PDF} className="h-6 w-6 " />
        <p className="text-center text-green-600 text-sm ml-4" key={index}>
          {item.name}
        </p>
      </div>
      <div className="flex justify-end">
        <FaRegTrashAlt
          size={"0.7em"}
          onClick={() => removeAll()}
          className=" text-red-500"
        />
      </div>
    </div>
  ));

  const removeAll = () => {
    setFile([]);
  };

  const handlePayment = async () => {
    const data = { title: title, file_path: file[0] };
    if (title && file.length > 0) {
      setIsLoading(true);
      try {
        const res: any = await Api.post(
          `/lawyer/payments/${paymentID?.id}/upload-payment-proof`,
          data,
          getFormDataHeader(),
        );

        if (res) {
          toast.success(`proof of payment upload is succefful`, {
            duration: 3000,
          });

          setTitle("");
          setFile([]);
          setOpen(false);
          setIsLoading(false);
          navigate("/lawyer/my-files");
        }
      } catch (error: any) {
        setIsLoading(false);
        // console.log(error?.response);
        toast.error(error?.response?.data?.message, {
          duration: 3000,
        });
      }
    } else {
      setIsLoading(false);
      setRequired("Kindly fill the title");
      setRequiredPhoto("Kindly upload an image");
    }
  };

  return (
    <>
      <div className="bg-[#F8F8F9E5]/90  w-full py-2 px-2 lg:px-8">
        <Typography textStyle="text-base">{`My Files  / ${caseData?.title}`}</Typography>
      </div>
      <div className="pt-4 pb-8 px-2 lg:px-8">
        <Typography textStyle="text-[20px] font-medium">
          Party Information
        </Typography>
        <div className="my-2">
          {caseData?.parties?.length > 0 ? (
            <TableComponent
              className="rounded-none rounded-t-0"
              DATA={caseData?.parties}
              COLUMNS={PARTY_COLUMNS_view}
              TITLE=""
              isExport={null}
              isPaginated={null}
              isSearch={null}
              size={size}
              setSize={setSize}
              setPageIndex={(value: number) => page + 1}
              isLoading={isLoadingCaseFolder}
            />
          ) : (
            <Typography variant="label" textStyle="font-medium text-center">
              {isLoadingCaseFolder ? "Loading..." : "No Party Information"}
            </Typography>
          )}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <Typography textStyle="text-[20px] font-medium">
            Uploaded Documents
          </Typography>
          <div className="inline-flex p-1 bg-gray-100 rounded-lg text-xs font-medium border border-gray-200">
            <button
              type="button"
              onClick={() => setDocTab("ALL")}
              className={`px-3 py-1.5 rounded-md transition-all ${
                docTab === "ALL"
                  ? "bg-white text-brand_indigo shadow-sm font-semibold"
                  : "text-gray-600 hover:text-black"
              }`}
            >
              All Documents ({caseData?.case_documents?.length || 0})
            </button>
            <button
              type="button"
              onClick={() => setDocTab("CLAIMANT")}
              className={`px-3 py-1.5 rounded-md transition-all ${
                docTab === "CLAIMANT"
                  ? "bg-white text-brand_indigo shadow-sm font-semibold"
                  : "text-gray-600 hover:text-black"
              }`}
            >
              Claimant Suit ({claimantDocs.length})
            </button>
            <button
              type="button"
              onClick={() => setDocTab("DEFENDANT")}
              className={`px-3 py-1.5 rounded-md transition-all ${
                docTab === "DEFENDANT"
                  ? "bg-white text-brand_indigo shadow-sm font-semibold"
                  : "text-gray-600 hover:text-black"
              }`}
            >
              Defendant Suit ({defendantDocs.length})
            </button>
          </div>
        </div>
        <div className="my-2 lg:flex justify-between items-center flex-wrap ">
          {displayedDocs?.length > 0 ? (
            displayedDocs.map((item: any, index: number) => (
              <div
                key={index}
                className="flex justify-between items-center mb-3 border border-gray-200 w-full lg:w-[48%] p-3 rounded-lg bg-white"
              >
                <div className="flex justify-start items-center ">
                  <a href={item?.file_path} target="_blank" rel="noreferrer">
                    <img src={PDF} className="h-6 w-6" alt="pdf" />
                  </a>
                  <div className="ml-4">
                    <div className="flex items-center gap-2">
                      <p className="text-black font-bold text-sm">
                        {item?.title}
                      </p>
                      {item?.party_title && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 capitalize">
                          {item.party_title}
                        </span>
                      )}
                    </div>
                    <p className="text-left text-gray-400 text-xs mt-1">
                      {moment(item?.created_at).format("MM/DD/YYYY")}
                    </p>
                  </div>
                </div>

                <div className=" ml-4 p-2 border rounded-lg">
                  <p className="text-center text-green-600 text-lg font-medium">
                    {formatCurrency({ iso: "en-ng", slug: "NGN" }).format(
                      item?.price || 0,
                    )}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <div className="py-6 w-full text-center">
              <Typography variant="label" textStyle="font-medium text-center text-gray-500">
                {isLoadingCaseFolder
                  ? "Loading..."
                  : docTab === "CLAIMANT"
                  ? "No Claimant Suit Documents Uploaded"
                  : docTab === "DEFENDANT"
                  ? "No Defendant Suit Documents Uploaded"
                  : "No Uploaded Documents"}
              </Typography>
            </div>
          )}
        </div>
        <div
          className={`${
            caseData?.case_status?.remark ? "justify-between" : "justify-end"
          } flex  items-end`}
        >
          {caseData?.case_status?.remark && (
            <div className="w-full lg:w-[40%]">
              <Typography textStyle="text-[20px] font-medium">
                Remarks
              </Typography>
              <div className="my-2 flex justify-start items-center ">
                <p className="bg-[#D9D9D91A] overflow-auto min-h-[100px] w-full p-4 text-sm text-gray-600 border border-gray-200">
                  {caseData?.case_status?.remark}
                </p>
              </div>
            </div>
          )}
          {isLoadingPayments ? (
            <div className="ml-4 w-[30%] flex justify-center items-center">
              <div className="w-6 h-6 border-2 border-gray-300 border-t-green-600 rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="ml-4 w-[30%]">
              <Typography textStyle="text-[20px] font-medium text-center">
                Total
              </Typography>
              <p className="text-center text-green-600 bg-[#D9D9D91A] text-2xl text-semibold  p-2  min-w-44 rounded-lg">
                {formatCurrency({ iso: "en-ng", slug: "NGN" }).format(
                  paymentID?.amount ? paymentID?.amount : 0,
                )}
              </p>
            </div>
          )}
        </div>
        <div className=" flex justify-between items-end mt-10">
          <div className="w-[45%] md:w-44">
            <Button
              iconClassName="h-[0.9rem] w-3"
              title="Back"
              bgColor={"bg-brand_indigo"}
              onClick={() => navigate("/lawyer/my-files")}
              hoverColor={""}
              textColor={"text-white"}
              className={"py-3 w-full"}
            />
          </div>
          {caseData?.payment_status !== "success" && isLoadingPayments ? (
            <div className="w-[45%] md:w-44">
              <Button
                isLoading={true}
                iconClassName="h-[0.9rem] w-3"
                title="Make Payment"
                bgColor={"bg-green-500"}
                onClick={() => null}
                hoverColor={""}
                textColor={"text-white"}
                className={"py-3 w-full"}
              />
            </div>
          ) : paymentID?.url && caseData?.payment_status !== "success" ? (
            <div className="w-[45%] md:w-44">
              <Button
                iconClassName="h-[0.9rem] w-3"
                title="Make Payment"
                bgColor={"bg-green-500"}
                onClick={() => (window.location.href = paymentID?.url)}
                hoverColor={""}
                textColor={"text-white"}
                className={"py-3 w-full"}
              />
            </div>
          ) : null}
          {/* {getCase?.require_payment && getCase?.payment_status !== "success" ? (
            <div className="w-[53%] md:w-44">
              <Button
                iconClassName="h-[0.9rem] w-3"
                title="Make payment"
                bgColor={"bg-[#27AE60]"}
                onClick={() => setOpen(true)}
                hoverColor={""}
                textColor={"text-white"}
                className={"py-3 w-full"}
              />
            </div>
          ) : getCase?.require_payment &&
            getCase?.payment_status === "pending" ? (
            <p>Your payment have been made</p>
          ) : null} */}
        </div>
        <Modal
          open={open}
          close={() => {
            setOpen(false);

            setFile([]);
          }}
          maxWidth="md"
        >
          <>
            <div className="px-8 pb-6">
              <Fade bottom>
                <>
                  <div className="">
                    <div className="flex flex-col   w-full lg:w-[48%]">
                      <label htmlFor="email" className="text-sm">
                        Title <small className="text-red-500">*</small>
                      </label>
                      <FormInput
                        type="text"
                        placeholder="Enter your title"
                        value={title}
                        onChange={(
                          text: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          setTitle(text.target.value);
                          setRequired("");
                        }}
                        className="border py-2 border-gray-200  px-4 mt-4 rounded-lg outline-none w-full"
                      />
                      <small className="text-red-500">{required}</small>
                    </div>
                  </div>
                  <div className=" py-2 md:py-4" {...getRootProps()}>
                    <div className="w-full">
                      {file.length > 0 ? (
                        productFiles
                      ) : (
                        <label className="flex flex-col justify-center items-center min-h-[250px] w-full px-4 transition border-2 border-gray-300 border-dashed rounded-md appearance-none cursor-pointer hover:border-gray-400 focus:outline-none">
                          <img src={UPLOAD} alt="" className="h-12" />
                          <p className="flex flex-col items-center space-x-2">
                            <span className="font-medium text-gray-600 mt-4 text-center text-sm">
                              Click to upload or drag and drop <br />
                              Maxiumum file size 10 MB
                            </span>
                            <a className="h-10 px-6 py-2 mt-4 lg:mt-0  ml-4 font-normal items-center text-black bg-BRAND-200 border border-BRAND-200 rounded-xl ">
                              Browse file
                            </a>
                          </p>
                          <span className="text-[#9CA3AF] text-xs mt-4 ">
                            Supported Image files only
                          </span>
                          <input
                            type="file"
                            name="file_upload"
                            className="hidden"
                            {...getInputProps()}
                          />
                        </label>
                      )}
                    </div>
                    <small className="text-red-500">{requiredPhoto}</small>
                  </div>

                  <div className="flex justify-start items-center mt-4">
                    <Button
                      icons={FLAG}
                      iconClassName="h-[0.9rem] w-3"
                      isLoading={loading}
                      title="Upload Document Info"
                      bgColor={"bg-brand_blue"}
                      onClick={() => handlePayment()}
                      hoverColor={""}
                      textColor={"text-white"}
                      className={"py-4 rounded-lg"}
                    />
                  </div>
                </>
              </Fade>
            </div>
          </>
        </Modal>
        <PartyInformation
          open={toggleStatus}
          close={() => dispatch(setToggle(false))}
          data={singlePartyInfo}
        />
      </div>
    </>
  );
}

export default ProceedPayment;
